import { CedeSDKError } from "./base";
import { SdkErrorCodes, SdkErrorMessages, SdkErrorNames } from "./errorsMetadata";

/**
 * @description Default error for trade
 */
export class TradeError extends CedeSDKError {
  constructor(code = SdkErrorCodes.TRADE_ERROR, message = SdkErrorMessages.TRADE_ERROR) {
    super(code, message);
    this.name = "TradeError";
  }
}

/**
 * @description Thrown when the order is not found
 */
export class OrderNotFoundError extends TradeError {
  constructor() {
    super(SdkErrorCodes.ORDER_NOT_FOUND, SdkErrorMessages.ORDER_NOT_FOUND);
    this.name = SdkErrorNames.ORDER_NOT_FOUND;
  }
}

/**
 * @description Thrown when the order is invalid
 */
export class InvalidOrderError extends TradeError {
  constructor() {
    super(SdkErrorCodes.INVALID_ORDER, SdkErrorMessages.INVALID_ORDER);
    this.name = SdkErrorNames.INVALID_ORDER;
  }
}

/**
 * @description Thrown when the order size or notional is below the exchange minimum
 */
export class OrderBelowMinimumError extends InvalidOrderError {
  constructor() {
    super();
    this.code = SdkErrorCodes.ORDER_BELOW_MINIMUM;
    this.message = SdkErrorMessages.ORDER_BELOW_MINIMUM;
    this.name = SdkErrorNames.ORDER_BELOW_MINIMUM;
  }
}

/**
 * @description Thrown when the order price violates exchange rules (tick size, distance from reference, etc.)
 */
export class OrderPriceInvalidError extends InvalidOrderError {
  constructor() {
    super();
    this.code = SdkErrorCodes.ORDER_PRICE_INVALID;
    this.message = SdkErrorMessages.ORDER_PRICE_INVALID;
    this.name = SdkErrorNames.ORDER_PRICE_INVALID;
  }
}

/**
 * @description Thrown when the market is not found (based on pair symbol)
 */
export class MarketNotFoundError extends TradeError {
  constructor(market: string) {
    super(SdkErrorCodes.MARKET_NOT_FOUND, SdkErrorMessages.MARKET_NOT_FOUND(market));
    this.name = "MarketNotFoundError";
  }
}

export class InvalidOHLCVFormatError extends TradeError {
  constructor(message: string) {
    super(SdkErrorCodes.INVALID_OHLCV_FORMAT, SdkErrorMessages.INVALID_OHLCV_FORMAT(message));
    this.name = "InvalidOHLCVFormatError";
  }
}

export class CannotProvideOHLCVError extends TradeError {
  constructor() {
    super(SdkErrorCodes.CANNOT_PROVIDE_OHLCV, SdkErrorMessages.CANNOT_PROVIDE_OHLCV);
    this.name = "CannotProvideOHLCVError";
  }
}

export class MarketError extends TradeError {
  constructor(code = SdkErrorCodes.MARKET_ERROR, message = SdkErrorMessages.MARKET_ERROR) {
    super(code, message);
    this.name = "MarketError";
  }
}

export class MarketNotLoadedError extends MarketError {
  constructor() {
    super(SdkErrorCodes.MARKET_NOT_LOADED, SdkErrorMessages.MARKET_NOT_LOADED);
    this.name = "MarketNotLoadedError";
  }
}

export class MarketTypeNotSupportedError extends MarketError {
  constructor(marketType: string) {
    super(SdkErrorCodes.MARKET_TYPE_NOT_SUPPORTED, SdkErrorMessages.MARKET_TYPE_NOT_SUPPORTED(marketType));
    this.name = "MarketTypeNotSupportedError";
  }
}

export class InvalidStpTypeError extends TradeError {
  constructor(stpType: string) {
    super(SdkErrorCodes.INVALID_STP_TYPE, SdkErrorMessages.INVALID_STP_TYPE(stpType));
    this.name = "InvalidStpTypeError";
  }
}

/**
 * @description Thrown when an order with the same client order ID already exists
 */
export class DuplicateOrderIdError extends TradeError {
  constructor() {
    super(SdkErrorCodes.DUPLICATE_ORDER_ID, SdkErrorMessages.DUPLICATE_ORDER_ID);
    this.name = "DuplicateOrderIdError";
  }
}

/**
 * @description Thrown when a post-only order would be immediately filled
 */
export class OrderImmediatelyFillableError extends TradeError {
  constructor() {
    super(SdkErrorCodes.ORDER_IMMEDIATELY_FILLABLE, SdkErrorMessages.ORDER_IMMEDIATELY_FILLABLE);
    this.name = SdkErrorNames.ORDER_IMMEDIATELY_FILLABLE;
  }
}

/**
 * @description Thrown when an IOC order would not execute
 */
export class OrderNotFillableError extends TradeError {
  constructor() {
    super(SdkErrorCodes.ORDER_NOT_FILLABLE, SdkErrorMessages.ORDER_NOT_FILLABLE);
    this.name = "OrderNotFillableError";
  }
}

/**
 * @description Thrown when a futures/swap contract is unavailable for trading
 */
export class ContractUnavailableError extends TradeError {
  constructor() {
    super(SdkErrorCodes.CONTRACT_UNAVAILABLE, SdkErrorMessages.CONTRACT_UNAVAILABLE);
    this.name = "ContractUnavailableError";
  }
}

export class MaxLeverageOutOfBoundsError extends TradeError {
  constructor() {
    super(SdkErrorCodes.MAX_LEVERAGE_OUT_OF_BOUNDS, SdkErrorMessages.MAX_LEVERAGE_OUT_OF_BOUNDS);
    this.name = "MaxLeverageOutOfBoundsError";
  }
}

export class MarketRestrictedError extends TradeError {
  constructor() {
    super(SdkErrorCodes.MARKET_RESTRICTED, SdkErrorMessages.MARKET_RESTRICTED);
    this.name = "MarketRestrictedError";
  }
}

export class LeverageNotModifiedError extends TradeError {
  constructor() {
    super(SdkErrorCodes.LEVERAGE_NOT_MODIFIED, SdkErrorMessages.LEVERAGE_NOT_MODIFIED);
    this.name = "LeverageNotModifiedError";
  }
}

/**
 * @description Thrown when an order would breach the account's maximum position limit for the contract
 */
export class MaxPositionExceededError extends TradeError {
  constructor() {
    super(SdkErrorCodes.MAX_POSITION_EXCEEDED, SdkErrorMessages.MAX_POSITION_EXCEEDED);
    this.name = SdkErrorNames.MAX_POSITION_EXCEEDED;
  }
}

/**
 * @description Thrown when the exchange rejects a reduce-only order because it would not reduce an
 * existing position (position already closed, side flipped, or amount above the remaining size).
 * Expected client-state drift, not a system fault — excluded from Sentry.
 */
export class ReduceOnlyRejectedError extends TradeError {
  constructor() {
    super(SdkErrorCodes.REDUCE_ONLY_REJECTED, SdkErrorMessages.REDUCE_ONLY_REJECTED);
    this.name = SdkErrorNames.REDUCE_ONLY_REJECTED;
  }
}
